/*
===============================================================================================================
                                 Copyright 2021-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useState, useContext } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import BackArrow from '@/public/icons/back-arrow.svg';
import { LocaleContext } from '@/contexts/locale-context';
import { AuthContext } from '@/contexts/auth-context';
import { MerchantContext } from '@/contexts/merchant-context';
import { AdminContext } from '@/contexts/admin-context';
import { RoutingContext } from '@/contexts/routing-context';
import portalUtilitiesManager from '@/utilities/lui-portal-utilities-manager';
import UserMenuDropdown from '../user-menu-dropdown';

export default function HeaderBarMenu({ merchantName }) {
  const { clearAuth } = useContext(AuthContext);
  const { getNavigationRoutes } = useContext(MerchantContext);
  const {
    isAdmin, isAdminReviewMode, adminModeMerchantId, getAdminFeature, getAdminNavRoute,
  } = useContext(AdminContext);
  const routes = (isAdmin && !isAdminReviewMode) ? getAdminNavRoute() : getNavigationRoutes();
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const router = useRouter();
  const merchantId = router.query?.companyId;
  const { showInMenu, showInReviewMode, shouldRenderV2Navigation } = useContext(RoutingContext);

  const { translate, getLanguageOptions } = useContext(LocaleContext);
  const [userMenuPage, setUserMenuPage] = useState(0);

  const isMultipleLanguageEnabled = getAdminFeature('multipleLanguageSupport');

  const isWidgetModeActive = portalUtilitiesManager.isWidgetModeActive();

  const mainMenu = [];

  let urlPrefix = '';

  if (isAdminReviewMode) {
    urlPrefix = `/merchants/${adminModeMerchantId}`;
  } else if (!isAdmin) {
    urlPrefix = `/${merchantId}`;
  }

  const addRoutes = (routeArray) => {
    routeArray.forEach((route) => {
      if (route.url && showInMenu(route.url)) {
        if (!isAdminReviewMode || (isAdminReviewMode && showInReviewMode(route.url))) {
          mainMenu.push({ title: translate(route.title), onClick: () => router.push(`${urlPrefix}${route.url}`), closeOnClick: true });
        }
      }
      if (route.subItems) {
        addRoutes(route.subItems);
      }
    });
  };

  if (isMobile && !shouldRenderV2Navigation) {
    addRoutes(routes);
  }

  // merchant portal, non-widget mode
  if ((!isAdmin || isAdminReviewMode) && !isWidgetModeActive) {
    // for nav-v1 add Users option
    if (!shouldRenderV2Navigation) {
      mainMenu.push(
        { title: translate('users.pageTitle'), onClick: () => router.push(`${urlPrefix}/users`), closeOnClick: true },
      );
    }
    mainMenu.push(
      { title: translate('login.logout'), onClick: () => clearAuth() },
    );
  }

  // admin portal in mobile view
  if (isMobile && isAdmin && !isAdminReviewMode && !shouldRenderV2Navigation && !isWidgetModeActive) {
    mainMenu.push({ title: translate('login.logout'), onClick: () => clearAuth() });
  }
  // admin portal v2 nav mode
  if (shouldRenderV2Navigation && isAdmin && !isAdminReviewMode && !isWidgetModeActive) {
    mainMenu.push({ title: translate('login.logout'), onClick: () => clearAuth() });
  }

  if (isMultipleLanguageEnabled) {
    mainMenu.splice(mainMenu.length - 1, 0, { title: translate('constants.lng'), onClick: () => setUserMenuPage(1) });
  }

  const populateLanguageMenuItems = () => {
    const languageMenu = [
      { title: translate('constants.selectLng'), icon: BackArrow, onClick: () => setUserMenuPage(0) },
    ];

    getLanguageOptions().forEach((language) => {
      languageMenu.push({
        title: language.title,
        icon: language.icon,
        onClick: language.onClick,
      });
    });

    return languageMenu;
  };

  const getMenuItems = (currentPage) => {
    if (currentPage === 1) {
      return populateLanguageMenuItems();
    }

    return mainMenu;
  };

  return (
    <UserMenuDropdown
      userName={merchantName}
      menuItems={getMenuItems(userMenuPage)}
      dropdownDisabled={!mainMenu.length}
      onClickAway={() => setUserMenuPage(0)}
    />
  );
}

HeaderBarMenu.propTypes = {
  merchantName: PropTypes.string.isRequired,
};
